/** Readable detail list with optional inline corrections. Sending belongs to the composer. */
import { type FC, useState } from 'react';
import { Button } from '@/app/lib/shadcdn/button';

export interface SummaryLine {
  key: string;
  label: string;
  value: string;
  caption?: string;
  editable?: boolean;
}

export interface EditableSummaryProps {
  lines: SummaryLine[];
  onEdit: (key: string) => void;
  onValueChange?: (key: string, value: string) => void;
  editLabel: string;
  status?: 'draft' | 'committing' | 'confirmed' | 'failed';
  statusNote?: string;
  footnote?: string;
}

export const EditableSummary: FC<EditableSummaryProps> = ({
  lines,
  onEdit,
  onValueChange,
  editLabel,
  status = 'draft',
  statusNote,
  footnote,
}) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const busy = status === 'committing' || status === 'confirmed';

  return (
    <div>
      <div
        className="animate-fadeUp overflow-hidden rounded-lg border border-border bg-card"
        style={{ animationDelay: '60ms' }}
      >
        {lines.map((line) => {
          const locked = line.editable !== true || busy;
          const isEditing = !locked && editingKey === line.key;
          return (
            <div
              key={line.key}
              className="flex items-center gap-3 border-b border-border px-4.5 py-4 last:border-b-0"
            >
              <span className="w-[84px] flex-none text-sm text-muted-foreground-subtle">
                {line.label}
              </span>
              {isEditing ? (
                <input
                  className="w-full rounded-input border border-primary bg-muted px-2.5 py-1.5 text-base font-semibold text-foreground outline-none"
                  value={line.value}
                  // Focus follows the user's explicit "Edit" tap — element-level
                  // focus management, not page-load autofocus.
                  ref={(element) => element?.focus()}
                  onChange={(event) => onValueChange?.(line.key, event.target.value)}
                  onBlur={() => setEditingKey(null)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === 'Escape') {
                      setEditingKey(null);
                    }
                  }}
                />
              ) : (
                <span className="text-base font-semibold">
                  {line.value}
                  {line.caption ? (
                    <small className="block text-sm font-normal text-muted-foreground">
                      {line.caption}
                    </small>
                  ) : null}
                </span>
              )}
              {locked ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => {
                    setEditingKey(line.key);
                    onEdit(line.key);
                  }}
                >
                  {editLabel}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {status === 'failed' && statusNote ? (
        <div className="mt-2.5 text-center text-sm text-warning">{statusNote}</div>
      ) : null}
      {footnote ? (
        <div
          className="animate-fadeUp mt-2.5 text-center text-sm text-muted-foreground-subtle"
          style={{ animationDelay: '200ms' }}
        >
          {footnote}
        </div>
      ) : null}
    </div>
  );
};
